/**
 * Canvas ripple that fills its positioned parent and animates on press.
 *
 * Adapted from react-ink 6.5.4 (https://github.com/vigetlabs/react-ink),
 * MIT License, Copyright (c) 2015 Viget Labs. Differences from the original:
 * - The canvas is `pointer-events: none`, so it never intercepts clicks, hit-testing
 *   or the devtools element picker. Presses are picked up from the parent element.
 * - Pointer events replace the separate mouse/touch code paths.
 * - An imperative handle (`press`/`release`) allows triggering it from code, e.g. on
 *   keyboard activation, and `trigger="manual"` turns off the automatic listeners.
 */

import React from "react";
import { withRefProp } from "../../../../utils/withRefProp";
import {
  type RippleStore,
  createRippleStore,
  getBlotOpacity,
  getBlotScale,
  getBlotShiftX,
  getBlotShiftY,
  getMaxRadius,
} from "./rippleStore";

export interface RippleHandle {
  /** Starts a ripple at the given viewport coordinates, or the centre when omitted. */
  press: (clientX?: number, clientY?: number) => void;
  /** Releases the most recent held ripple so it fades out. */
  release: () => void;
}

export interface RippleProps {
  /** Fill the whole surface with a faint wash while pressed. @default true */
  background?: boolean;
  /** Duration of the full animation in ms. @default 1000 */
  duration?: number;
  /** Peak opacity of the ripple. @default 0.25 */
  opacity?: number;
  /** Max ripple radius in px. Defaults to 60% of the larger side. */
  radius?: number;
  /** Pull the ripple towards the centre as it grows. @default true */
  recenter?: boolean;
  /** Ripple colour. Defaults to the inherited text colour (`currentColor`). */
  color?: string;
  /**
   * `"auto"` listens for pointer presses on the parent element. `"manual"` only
   * ripples through the ref handle. @default "auto"
   */
  trigger?: "auto" | "manual";
  /** Ignore presses (the handle still works). */
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  ref?: React.Ref<RippleHandle>;
}

const TAU = Math.PI * 2;

const BASE_STYLE: React.CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  borderRadius: "inherit",
  pointerEvents: "none",
};

interface Surface {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  density: number;
  color: string;
}

export const Ripple = withRefProp((props: RippleProps) => {
  const {
    background = true,
    duration = 1000,
    opacity = 0.25,
    radius,
    recenter = true,
    color,
    trigger = "auto",
    disabled = false,
    className,
    style,
    ref,
  } = props;

  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const surfaceRef = React.useRef<Surface | null>(null);
  const storeRef = React.useRef<RippleStore | null>(null);
  const optionsRef = React.useRef({ background, duration, opacity, radius, recenter, color });

  React.useLayoutEffect(() => {
    optionsRef.current = { background, duration, opacity, radius, recenter, color };
  });

  const getStore = React.useCallback(() => {
    storeRef.current ??= createRippleStore(() => {
      const surface = surfaceRef.current;
      const store = storeRef.current;
      if (!surface || !store) return;

      const { ctx, width, height, density } = surface;
      const options = optionsRef.current;
      const size = Math.max(height, width);

      ctx.save();
      ctx.scale(density, density);
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = surface.color;

      if (options.background) {
        ctx.globalAlpha = store.getTotalOpacity(options.opacity);
        ctx.fillRect(0, 0, width, height);
      }

      for (const blot of store.blots()) {
        let { x, y } = blot;
        if (options.recenter) {
          x += getBlotShiftX(blot, size, width);
          y += getBlotShiftY(blot, size, height);
        }
        ctx.globalAlpha = getBlotOpacity(blot, options.opacity);
        ctx.beginPath();
        ctx.arc(x, y, blot.radius * getBlotScale(blot), 0, TAU);
        ctx.closePath();
        ctx.fill();
      }

      ctx.restore();
    });
    return storeRef.current;
  }, []);

  const press = React.useCallback(
    (clientX?: number, clientY?: number) => {
      const canvas = canvasRef.current;
      // getContext returns null in non-canvas environments (e.g. the test DOM).
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx) return;

      const options = optionsRef.current;
      const { top, left, width, height } = canvas.getBoundingClientRect();
      const density = window.devicePixelRatio || 1;

      // Resizing the backing store clears it, the next frame redraws any live blots.
      canvas.width = width * density;
      canvas.height = height * density;
      surfaceRef.current = {
        ctx,
        width,
        height,
        density,
        color: options.color ?? window.getComputedStyle(canvas).color,
      };

      getStore().add({
        duration: options.duration,
        mouseDown: Date.now(),
        mouseUp: 0,
        radius: getMaxRadius(height, width, options.radius),
        x: clientX === undefined ? width / 2 : clientX - left,
        y: clientY === undefined ? height / 2 : clientY - top,
      });
    },
    [getStore],
  );

  const release = React.useCallback(() => {
    storeRef.current?.release(Date.now());
  }, []);

  React.useImperativeHandle(ref, () => ({ press, release }), [press, release]);

  React.useEffect(() => {
    const host = canvasRef.current?.parentElement;
    if (trigger !== "auto" || disabled || !host) return;

    const onDown = (event: PointerEvent) => {
      // Primary button only, and not ctrl+click (context menu on macOS).
      if (event.button !== 0 || event.ctrlKey) return;
      press(event.clientX, event.clientY);
    };
    host.addEventListener("pointerdown", onDown);
    host.addEventListener("pointerup", release);
    host.addEventListener("pointerleave", release);
    host.addEventListener("pointercancel", release);
    return () => {
      host.removeEventListener("pointerdown", onDown);
      host.removeEventListener("pointerup", release);
      host.removeEventListener("pointerleave", release);
      host.removeEventListener("pointercancel", release);
    };
  }, [trigger, disabled, press, release]);

  React.useEffect(() => () => storeRef.current?.stop(), []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={className}
      style={style ? { ...BASE_STYLE, ...style } : BASE_STYLE}
    />
  );
});

export default Ripple;
